import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import allDoneIllustration from "../../../../assets/images/reading-review/all-done.png";
import SessionSelect from "./SessionSelect";
import ContinuePausedSession from "./ContinuePausedSession";
import SessionWorkspace from "./SessionWorkspace";
import { takeResumePoint } from "./resumePoint";
import {
  getReviewSession,
  setReviewSessionState,
  getApiErrorMessage,
} from "../../../../services/case-manager/readingReviewSession";
import Spinner from "../../../../components/spinner";
import styles from "./ReadingReviewSession.module.css";

const AllDone = ({ caseManagerName, onBack }) => (
  <div className={styles.centered}>
    <p className={styles.subheading} style={{ color: "#0d1139", marginBottom: 16 }}>
      All patients readings have been reviewed that were assigned to <strong>{caseManagerName}</strong>
    </p>
    <img src={allDoneIllustration} alt="" className={styles.illustration} />
    <button type="button" className={styles.primaryBtn} onClick={onBack}>
      Back to Selection
    </button>
  </div>
);

// SCRUM-844 Readings Review 2.0: a guided, pausable review session per case
// manager ("Reading Review: How It Works", Oct 1 2026):
//   select (845) → [paused? "Continue paused Session?" (850)] → workspace
//   (851, with the Session List 852) → "All Done" when the last patient is
//   finished. Pause / idle / leaving the tab pause the session (850).
// timerValues: the My Tasks monitoring tracker's live time, for the Notes
// panel - same as the classic Readings Review.
const ReadingReviewSession = ({ timerValues = {} }) => {
  const queryClient = useQueryClient();
  const accId = sessionStorage.getItem("acc_id");
  // Coming back from Patient Details: pick the session back up where it was.
  const [resumePoint] = useState(takeResumePoint);
  const [screen, setScreen] = useState(resumePoint ? { name: "resuming" } : { name: "select" });

  useEffect(() => {
    if (!resumePoint) return;
    (async () => {
      try {
        let session = await getReviewSession(accId, resumePoint.sessionId);
        // Leaving for Patient Details paused it; take it back unless it's
        // still ours and active.
        if (!(session.status === "active" && session.is_mine)) {
          session = await setReviewSessionState(accId, resumePoint.sessionId, "continue");
        }
        setScreen({ name: "workspace", session, restore: resumePoint });
      } catch (error) {
        // Locked by someone else, or completed/abandoned meanwhile.
        toast.error(getApiErrorMessage(error, "That review session is no longer available."));
        setScreen({ name: "select" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toSelection = useCallback(() => {
    // Case manager sessions/locks changed - don't show a stale list.
    queryClient.invalidateQueries(["reading-review", "case-managers"]);
    setScreen({ name: "select" });
  }, [queryClient]);

  const toWorkspace = useCallback((session) => setScreen({ name: "workspace", session }), []);
  const toDone = useCallback((session) => setScreen({ name: "done", session }), []);

  if (screen.name === "workspace") {
    return (
      <SessionWorkspace
        key={screen.session.id}
        accId={accId}
        initialSession={screen.session}
        timerValues={timerValues}
        restore={screen.restore}
        onExit={toSelection}
        onCompleted={toDone}
      />
    );
  }

  if (screen.name === "resuming") {
    return (
      <div className={styles.screen} style={{ alignItems: "center", justifyContent: "center" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <h2 className={styles.screenTitle}>Readings Review</h2>
      {screen.name === "prompt" ? (
        <ContinuePausedSession
          accId={accId}
          session={screen.session}
          caseManager={screen.caseManager}
          locationIds={screen.locationIds}
          onEnterSession={toWorkspace}
          onBack={toSelection}
        />
      ) : screen.name === "done" ? (
        <AllDone caseManagerName={screen.session.case_manager?.name} onBack={toSelection} />
      ) : (
        <SessionSelect
          onEnterSession={toWorkspace}
          onPausedSession={({ session, caseManager, locationIds }) =>
            setScreen({ name: "prompt", session, caseManager, locationIds })
          }
        />
      )}
    </div>
  );
};

export default ReadingReviewSession;
